import { Option, safeExecute, tryCatch, UUID } from "@opendaw/lib-std";
import { ProjectSkeleton } from "@opendaw/studio-adapters";
import { Promises } from "@opendaw/lib-runtime";
import { ProjectMeta } from "./ProjectMeta";
import { ProjectProfile } from "./ProjectProfile";
import { Workers } from "../Workers";
import { TemplatePaths } from "./TemplatePaths";
export var TemplateStorage;
(function (TemplateStorage) {
    TemplateStorage.listTemplates = async ({ includeCover, includeProject, progress } = {}) => {
        return Workers.Opfs.list(TemplatePaths.Folder)
            .then(files => Promise.all(files.filter(file => file.kind === "directory")
            .map(async ({ name }, index, { length }) => {
            safeExecute(progress, (index + 1) / length);
            const uuid = UUID.parse(name);
            const array = await Workers.Opfs.read(TemplatePaths.projectMeta(uuid));
            return {
                uuid,
                meta: ProjectMeta.fromJSON(JSON.parse(new TextDecoder().decode(array))),
                cover: includeCover ? (await TemplateStorage.loadCover(uuid)).unwrapOrUndefined() : undefined,
                project: includeProject ? await TemplateStorage.loadTemplate(uuid) : undefined
            };
        })));
    };
    TemplateStorage.exists = async (uuid) => (await Promises.tryCatch(Workers.Opfs.read(TemplatePaths.projectMeta(uuid)))).status === "resolved";
    TemplateStorage.loadTemplate = async (uuid) => {
        return Workers.Opfs.read(TemplatePaths.projectFile(uuid)).then(array => array.buffer);
    };
    TemplateStorage.loadMeta = async (uuid) => {
        return Workers.Opfs.read(TemplatePaths.projectMeta(uuid)).then(array => array.buffer);
    };
    TemplateStorage.loadCover = async (uuid) => {
        return Workers.Opfs.read(TemplatePaths.projectCover(uuid))
            .then(array => Option.wrap(array.buffer), () => Option.None);
    };
    TemplateStorage.saveAsTemplate = async (profile, name) => {
        const uuid = UUID.generate();
        const project = profile.project.copy();
        const meta = ProjectMeta.copy(profile.meta);
        delete meta.radioToken; // never carry a publish token into a template
        const template = new ProjectProfile(uuid, project, meta, profile.cover, true);
        template.updateMetaData("name", name);
        template.updateMetaData("created", new Date().toISOString());
        template.updateModifyDate();
        await Workers.Opfs.write(TemplatePaths.projectFile(uuid), new Uint8Array(project.toArrayBuffer()));
        await Workers.Opfs.write(TemplatePaths.projectMeta(uuid), new TextEncoder().encode(JSON.stringify(template.meta)));
        await profile.cover.match({
            none: () => Promise.resolve(),
            some: cover => Workers.Opfs.write(TemplatePaths.projectCover(uuid), new Uint8Array(cover))
        });
    };
    TemplateStorage.listUsedAssets = async (type) => {
        console.debug("listUsedAssets", type.name);
        const result = new Map();
        const exactBuffer = (bytes) => bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength);
        const files = await Workers.Opfs.list(TemplatePaths.Folder);
        for (const { name: folder } of files.filter(file => file.kind === "directory")) {
            const uuid = UUID.parse(folder);
            const projectBytes = await Promises.tryCatch(Workers.Opfs.read(TemplatePaths.projectFile(uuid)));
            if (projectBytes.status === "rejected") {
                continue;
            }
            const metaBytes = await Promises.tryCatch(Workers.Opfs.read(TemplatePaths.projectMeta(uuid)));
            const templateName = metaBytes.status === "rejected" ? folder
                : ProjectMeta.fromJSON(JSON.parse(new TextDecoder().decode(metaBytes.value))).name;
            const decoded = tryCatch(() => ProjectSkeleton.decode(exactBuffer(projectBytes.value)));
            if (decoded.status === "failure") {
                console.warn(`listUsedAssets: failed to decode template '${templateName}'`, decoded.error);
                continue;
            }
            for (const box of decoded.value.boxGraph.boxes()) {
                if (!(box instanceof type)) {
                    continue;
                }
                const key = UUID.toString(box.address.uuid);
                const list = result.get(key) ?? [];
                if (!list.includes(templateName)) {
                    list.push(templateName);
                }
                result.set(key, list);
            }
        }
        return result;
    };
    TemplateStorage.deleteTemplate = async (uuid) => {
        const array = await TemplateStorage.loadTrashedIds();
        array.push(UUID.toString(uuid));
        const trash = new TextEncoder().encode(JSON.stringify(array));
        await Workers.Opfs.write(`${TemplatePaths.Folder}/trash.json`, trash);
        await Workers.Opfs.delete(TemplatePaths.templateFolder(uuid));
    };
    TemplateStorage.loadTrashedIds = async () => {
        const { status, value } = await Promises.tryCatch(Workers.Opfs.read(`${TemplatePaths.Folder}/trash.json`));
        return status === "rejected" ? [] : JSON.parse(new TextDecoder().decode(value));
    };
})(TemplateStorage || (TemplateStorage = {}));
